import dayjs from "dayjs";
import { sumBy } from "lodash";

export const PROFILE_SECTIONS = {
  profile: "profile",
  bookings: "bookings",
};

export const BOOKING_DATE_TYPES = [
  { value: 1, label: "Booking Date" },
  { value: 2, label: "Travel Date" },
];

export const TITLE_OPTIONS = ["Mr", "Mrs", "Ms"];

export function isProfileSuccess(response) {
  const status = String(response?.status || "").toLowerCase();
  return status === "success" || status === "true";
}

export function normalizeUserProfile(raw = {}) {
  const data = raw?.data ?? raw ?? {};

  return {
    firstName: data.firstName || data.FirstName || "",
    lastName: data.lastName || data.LastName || "",
    gender: data.gender || data.Gender || data.title || data.Title || "",
    countryId: Number(data.countryId || data.CountryId || 0) || 0,
    cityId: Number(data.cityId || data.CityId || 0) || 0,
    dob: data.dob || data.DOB || data.dateOfBirth || "",
    emailId: data.emailId || data.email || data.Email || "",
    countryCode: data.countryCode || data.CountryCode || "",
    mobileNumber: data.mobileNumber || data.mobileNo || data.Phone || "",
    fullName:
      data.fullName ||
      data.name ||
      [data.firstName || data.FirstName, data.lastName || data.LastName]
        .filter(Boolean)
        .join(" "),
  };
}

export function buildUpdateUserPayload(form, token) {
  return {
    token,
    firstName: form.firstName?.trim() || "",
    lastName: form.lastName?.trim() || "",
    gender: form.gender || "",
    countryId: Number(form.countryId) || 0,
    cityId: Number(form.cityId) || 0,
    dob: form.dob ? dayjs(form.dob).format("YYYY-MM-DD") : "",
    emailId: form.emailId?.trim() || "",
    countryCode: form.countryCode || "",
    mobileNumber: form.mobileNumber?.trim() || "",
  };
}

export function formatProfileDob(value) {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
}

export function formatBookingDateTime(value) {
  if (!value) return "—";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD MMM, YYYY [at] hh:mm A") : value;
}

export function formatBookingDate(value) {
  if (!value) return "—";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD MMM, YYYY") : value;
}

export function isBookingConfirmedStatus(status = "") {
  const value = String(status).toLowerCase().trim();
  return value.includes("confirm") && !value.includes("pending");
}

export function normalizeBookingsList(response) {
  const list = Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response)
      ? response
      : [];

  return list.map((item, index) => ({
    id: item.id || item.bookingId || item.referenceNo || index,
    referenceNo:
      item.referenceNo ||
      item.referenceNumber ||
      item.bookingReference ||
      item.bookingRefNo ||
      item.bookingRefId ||
      item.BookingRefId ||
      "—",
    refIdKey:
      item.refIdKey ||
      item.RefIdKey ||
      item.refKey ||
      item.RefKey ||
      item.referenceKey ||
      item.ReferenceKey ||
      "",
    bookingId: item.bookingId || item.BookingId || item.id || 0,
    serviceName:
      item.serviceName ||
      item.packageName ||
      item.productName ||
      item.title ||
      "—",
    bookedTime:
      item.bookedTime ||
      item.bookingDate ||
      item.bookingTime ||
      item.createdDate ||
      item.bookedOn ||
      "",
    status: item.status || item.bookingStatus || item.BookingStatus || "—",
    type: item.type || item.moduleType || item.bookingType || "—",
    travelDate:
      item.travelDate || item.travelStartDate || item.journeyDate || "",
    viewUrl: item.viewUrl || item.detailUrl || "",
  }));
}

export function normalizeBookingDetailsResponse(response) {
  const payload =
    response?.data && typeof response.data === "object" && !Array.isArray(response.data)
      ? response.data
      : response || {};

  const bookingDetails = Array.isArray(payload.bookingDetails)
    ? payload.bookingDetails
    : Array.isArray(payload.BookingDetails)
      ? payload.BookingDetails
      : [];

  const customerDetail =
    payload.customerDetail || payload.CustomerDetail || {};

  const bookingReference =
    payload.bookingReference ||
    payload.BookingReference ||
    payload.referenceNo ||
    payload.referenceNumber ||
    "";

  const bookingId =
    payload.bookingId ??
    payload.BookingId ??
    customerDetail.bookingId ??
    customerDetail.BookingId ??
    bookingDetails[0]?.bookingId ??
    bookingDetails[0]?.BookingId ??
    bookingDetails[0]?.bookingDId ??
    0;

  return {
    bookingReference,
    bookingId,
    customerDetail,
    bookingDetails,
    status:
      payload.status ||
      payload.bookingStatus ||
      payload.BookingStatus ||
      bookingDetails[0]?.status ||
      bookingDetails[0]?.bookingStatus ||
      "",
  };
}

export function getBookingPackageName(booking = {}) {
  return (
    booking.packageName ||
    booking.activityName ||
    booking.serviceName ||
    booking.staycationName ||
    booking.visaName ||
    ""
  );
}

export function getBookingDestinationLabel(booking = {}) {
  if (booking.destinationAndStay?.length) {
    return booking.destinationAndStay
      .map((city) => `${city.nights || 0}N ${city.cityName || city.city || ""}`)
      .filter(Boolean)
      .join(" | ");
  }

  return booking.city || booking.destination || booking.optionName || "—";
}

// export function getBookingInclusionLabel(booking = {}) {
//   if (Array.isArray(booking.inclusion) && booking.inclusion.length) {
//     return booking.inclusion.join(", ");
//   }

//   return booking.inclusionText || booking.inclusions || "—";
// }
const HOLIDAY_SERVICE_LABELS = [
  { key: "flightIncluded", label: "Flight" },
  { key: "mealsIncluded", label: "Meals" },
  { key: "transferIncluded", label: "Transfer" },
  { key: "hotelIncluded", label: "Hotel" },
  { key: "activityIncluded", label: "Activity" },
  { key: "visaIncluded", label: "VISA" },
];

export function getBookingInclusionLabel(item = {}) {
  const labels = HOLIDAY_SERVICE_LABELS.filter(
    (service) => item[service.key] === true || item[service.key] === "true"
  ).map((service) => service.label);

  if (labels.length) return labels.join(", ");
  return labels;
}

export function getBookingTravelersLabel(booking = {}) {
  const parts = [];

  if (booking.noOfAdult) {
    parts.push(`${booking.noOfAdult} Adult${booking.noOfAdult > 1 ? "s" : ""}`);
  }

  if (booking.noOfChild) {
    parts.push(`${booking.noOfChild} Child${booking.noOfChild > 1 ? "ren" : ""}`);
  }

  return parts.length ? parts.join(", ") : "—";
}

export function getBookingTravelDateLabel(booking = {}) {
  if (!booking.travelDate) return "—";

  const start = dayjs(booking.travelDate);
  if (!start.isValid()) return booking.travelDate;

  const end = booking.destinationAndStay?.length
    ? start.add(sumBy(booking.destinationAndStay, "nights"), "day")
    : null;

  if (end?.isValid()) {
    return `${start.format("DD MMM, YYYY")} - ${end.format("DD MMM, YYYY")}`;
  }

  return start.format("DD MMM, YYYY");
}

export function formatMobileDisplay(countryCode = "", mobileNumber = "") {
  const code = String(countryCode || "").replace(/^\+/, "");
  const number = String(mobileNumber || "").trim();
  if (!number) return "—";
  if (!code) return number;
  return `+${code} ${number}`;
}
